import React from 'react';
import { HeartHandshake, HelpCircle, FileText } from 'lucide-react';

/**
 * Компонент раздела формы для параметров вычета на благотворительность
 */
const CharityDeductionSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  // Расчет максимальной суммы пожертвований (25% от дохода)
  const maxDonationAmount = parseFloat(formData.incomeAmount) * 0.25;
  
  return (
    <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <HeartHandshake className="w-5 h-5 mr-2 text-purple-500" />
        Параметры вычета на благотворительность
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Сумма пожертвований */}
        <div>
          <label htmlFor="charityDonations" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Сумма пожертвований
          </label>
          <input
            type="text"
            id="charityDonations"
            name="charityDonations"
            value={formData.charityDonations}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.charityDonations ? 'input-error' : ''}`}
            placeholder="Введите сумму пожертвований"
          />
          {errors.charityDonations && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.charityDonations}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Максимальная сумма к вычету: 25% от вашего годового дохода ({new Intl.NumberFormat('ru-RU').format(maxDonationAmount)} ₽)
          </p>
        </div>

        {/* Вторая колонка - информация */}
        <div className="flex flex-col justify-center">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/40 rounded-lg border border-purple-200 dark:border-purple-800">
            <p className="text-xs text-purple-700 dark:text-purple-300">
              <strong>Лимит вычета:</strong> 25% от вашего годового дохода.
              <br />
              <strong>Возможный возврат:</strong> {new Intl.NumberFormat('ru-RU').format(Math.min(parseFloat(formData.charityDonations || 0), maxDonationAmount) * 0.13)} ₽
            </p>
          </div>
        </div>
      </div>

      {/* Информационный блок */}
      <div className="mt-4 flex items-start p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
        <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
            Важно знать о благотворительном вычете
          </h4>
          <div className="text-xs text-blue-600 dark:text-blue-400">
            <p className="mb-1">
              Социальный вычет на благотворительность предоставляется при пожертвованиях:
            </p>
            <ul className="list-disc pl-5 mb-2 space-y-0.5">
              <li>Благотворительным организациям</li>
              <li>Религиозным организациям на уставную деятельность</li>
              <li>Некоммерческим организациям на формирование целевого капитала</li>
              <li>Государственным учреждениям (образовательным, культурным, медицинским)</li>
            </ul>
            <p>
              Максимальный размер вычета ограничен 25% от вашего облагаемого дохода за год.
            </p>
          </div>
        </div>
      </div>

      {/* Перечень документов */}
      <div className="mt-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <FileText className="w-4 h-4 mr-2 text-purple-500" />
          Необходимые документы:
        </h4>
        <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc pl-5 space-y-1">
          <li>Платежные документы (квитанции, чеки, платежные поручения)</li>
          <li>Договор пожертвования (если заключался)</li>
          <li>Учредительные документы организации-получателя, подтверждающие право на получение пожертвований</li>
          <li>Справка от религиозной организации о направлении пожертвования на уставную деятельность (в случае пожертвования религиозным организациям)</li>
        </ul>
      </div>

      {/* Примечание о возможных ограничениях */}
      <div className="mt-4 p-3 border border-dashed border-purple-300 dark:border-purple-700 rounded-lg">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          <strong>Обратите внимание:</strong> Пожертвования, сделанные наличными без подтверждающих документов, не учитываются для налогового вычета. 
          Также пожертвования физическим лицам, в том числе через краудфандинговые платформы, не дают права на получение вычета, если они не направлены 
          через благотворительные фонды или некоммерческие организации.
        </p>
      </div>
    </div>
  );
};

export default CharityDeductionSection;